import type { LocationQueryRaw } from '#vue-router'
import type { Booking } from '@brighterly/lib-core-types/booking/interfaces'

import { Status } from '@brighterly/lib-core-types/booking/enums'

import { usePaidStore } from '../store'
import { useShowLessonApi } from '../../api/show-lesson.hook'
import { lsFirstLessonIntroSkipped } from '../../lib/onboarding/first-lesson-storage'

const QUERY_ACTION = 'action'
const QUERY_BOOKING_ID = 'booking_id'

const ACTION_ATTEND = 'attend'
const ACTION_RESCHEDULE = 'reschedule'

/** Statuses where the lesson is still ahead — anything else means there is nothing to confirm or move. */
const UPCOMING_STATUSES: Status[] = [Status.Planned, Status.Confirmed]

const isUpcoming = (booking: Booking): boolean => UPCOMING_STATUSES.includes(booking.status)

export const useFirstLessonEmailAction = (): void => {
  const route = useRoute()
  const router = useRouter()

  const paidStore = usePaidStore()

  const { showLesson } = useShowLessonApi()

  const action = (route.query[QUERY_ACTION] || null) as string | null
  const bookingId = Number(route.query[QUERY_BOOKING_ID])
  const hasEmailAction = !!action && !!bookingId

  // Claimed during setup — the onboarding modals evaluate their gates while this hook is still fetching,
  // so without the claim they would open underneath and outlive the attend modal on the intro step.
  if (hasEmailAction) {
    paidStore.setFirstLessonAttendPending(true)
  }

  const clearQueryParams = async (): Promise<void> => {
    const query: LocationQueryRaw = {
      ...route.query,
      [QUERY_ACTION]: undefined,
      [QUERY_BOOKING_ID]: undefined,
    }

    try {
      await router.replace({ query })
    } catch (err) {
      console.error('unable to clear first lesson email action params:', err)
    }
  }

  const onAttend = (booking: Booking): void => {
    if (!isUpcoming(booking)) return

    // Written before the modal opens so the onboarding intro step is already skipped when that flow syncs.
    lsFirstLessonIntroSkipped.value = true
    paidStore.setModalFirstLessonAttend(booking)
  }

  const onReschedule = (booking: Booking): void => {
    if (!isUpcoming(booking) || !booking.rescheduled_allowed) return

    paidStore.setModalReschedule(booking)
  }

  // The lesson request is not cancellable, and the paid store outlives this page — without the guard a
  // response arriving after the customer navigated away would open a stale modal on their next home visit.
  let isDisposed = false

  onMounted(async () => {
    if (!hasEmailAction) return

    try {
      await clearQueryParams()
      if (isDisposed) return

      const result = await showLesson(bookingId)
      if (isDisposed || !result?.booking) return

      if (action === ACTION_ATTEND) {
        onAttend(result.booking)
      } else if (action === ACTION_RESCHEDULE) {
        onReschedule(result.booking)
      }
    } finally {
      if (!isDisposed) {
        paidStore.setFirstLessonAttendPending(false)
      }
    }
  })

  onBeforeUnmount(() => {
    isDisposed = true
    paidStore.setFirstLessonAttendPending(false)
    paidStore.setModalFirstLessonAttend(null)
  })
}
